'use client';

import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import Link from 'next/link';
import type { MenuAssignment, MenuNode } from '@/lib/navigation';
import PublicExternalLink from './PublicExternalLink';

type Props = {
  tree: MenuNode[];
  rootAssignments?: MenuAssignment[];
};

type SearchResults = {
  pages: Array<{ id: number; title: string; slug: string }>;
  forms: Array<{ id: number; name: string; slug: string }>;
  externalLinks: Array<{
    id: number;
    name: string;
    slug: string;
    url: string;
    open_behavior: string;
    show_popup_note: boolean;
    popup_note_html: string | null;
    clipboard_text: string | null;
  }>;
};

const MIN_SEARCH_LENGTH = 2;

function MenuButtonIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="6" width="16" height="2" rx="1" fill="currentColor" />
      <rect x="4" y="11" width="16" height="2" rx="1" fill="currentColor" />
      <rect x="4" y="16" width="16" height="2" rx="1" fill="currentColor" />
    </svg>
  );
}

function CaretDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  const maskStyle = {
    display: 'inline-block',
    backgroundColor: 'currentColor',
    mask: "url('/icons/search_icon.svg') center / contain no-repeat",
    WebkitMask: "url('/icons/search_icon.svg') center / contain no-repeat",
  } as const;
  return (
    <span className={className} style={maskStyle} aria-hidden="true" />
  );
}

type MenuEntry =
  | { kind: 'menu'; node: MenuNode }
  | { kind: 'assignment'; item: MenuAssignment };

function buildEntries(node: MenuNode): MenuEntry[] {
  const childEntries = node.children.map(child => ({
    kind: 'menu' as const,
    node: child,
    order_index: child.order_index ?? 0,
  }));
  const assignmentEntries = node.assignments.map(item => ({
    kind: 'assignment' as const,
    item,
    order_index: item.order_index ?? 0,
  }));
  return [...childEntries, ...assignmentEntries]
    .sort((a, b) => {
      if (a.order_index === b.order_index) {
        if (a.kind === b.kind) {
          if (a.kind === 'menu' && b.kind === 'menu') {
            return a.node.id - b.node.id;
          }
          if (a.kind === 'assignment' && b.kind === 'assignment') {
            return a.item.id - b.item.id;
          }
        }
        return a.kind.localeCompare(b.kind);
      }
      return a.order_index - b.order_index;
    })
    .map(entry => (entry.kind === 'menu' ? { kind: 'menu', node: entry.node } : { kind: 'assignment', item: entry.item }));
}

const MAX_SUBMENU_COLUMNS = 3;

function hasSubmenu(node: MenuNode) {
  return node.children.length > 0 || node.assignments.length > 0;
}

function createEmptyActiveNodes() {
  return Array.from({ length: MAX_SUBMENU_COLUMNS }, () => null) as Array<MenuNode | null>;
}

function resolveAssignmentLink(item: MenuAssignment) {
  if (item.type === 'external') {
    const behavior = item.open_behavior || 'current';
    if (behavior === 'embed') {
      return { href: `/x/${item.slug}` };
    }
    const href = item.url || '#';
    if (behavior === 'new') {
      return { href, target: '_blank', rel: 'noreferrer' };
    }
    return { href };
  }
  if (item.type === 'report') return { href: `/r/${item.slug}` };
  if (item.type === 'lookup') {
    return { href: `/l/${item.slug}` };
  }
  return { href: item.type === 'page' ? `/p/${item.slug}` : `/f/${item.slug}` };
}

export default function NavSearchBar({ tree, rootAssignments }: Props) {
  const topAssignments = Array.isArray(rootAssignments) ? rootAssignments : [];
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeNodes, setActiveNodes] = useState<Array<MenuNode | null>>(() => createEmptyActiveNodes());
  const [drillStack, setDrillStack] = useState<MenuNode[]>([]);
  const [isNarrow, setIsNarrow] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>({ pages: [], forms: [], externalLinks: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const pointerTypeRef = useRef<string>('mouse');

  const term = query.trim();
  const showResults = searchOpen && term.length >= MIN_SEARCH_LENGTH;
  const hasPageResults = results.pages.length > 0;
  const hasFormResults = results.forms.length > 0;
  const hasExternalLinkResults = results.externalLinks.length > 0;
  const hasAnySearchResults = hasPageResults || hasFormResults || hasExternalLinkResults;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(max-width: 900px)');
    const update = () => setIsNarrow(mediaQuery.matches);
    update();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }
    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  useEffect(() => {
    function handleDocClick(event: MouseEvent) {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(target)) {
        setSearchOpen(false);
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setSearchOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) {
      setActiveNodes(createEmptyActiveNodes());
      setDrillStack([]);
    }
  }, [menuOpen]);

  useEffect(() => {
    setActiveNodes(createEmptyActiveNodes());
    setDrillStack([]);
  }, [isNarrow]);

  useEffect(() => {
    if (term.length < MIN_SEARCH_LENGTH) {
      setResults({ pages: [], forms: [], externalLinks: [] });
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(`/api/search?q=${encodeURIComponent(term)}`, { signal: controller.signal });
        if (!res.ok) {
          setError('Search failed.');
          setResults({ pages: [], forms: [], externalLinks: [] });
          setLoading(false);
          return;
        }
        const data = (await res.json()) as SearchResults;
        setResults({
          pages: Array.isArray(data.pages) ? data.pages : [],
          forms: Array.isArray(data.forms) ? data.forms : [],
          externalLinks: Array.isArray(data.externalLinks) ? data.externalLinks : [],
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError('Search failed.');
          setResults({ pages: [], forms: [], externalLinks: [] });
        }
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [term]);

  const topLevelEntries = useMemo<MenuEntry[]>(() => {
    const menuEntries: Array<{ kind: 'menu'; node: MenuNode; order_index: number }> = tree.map(node => ({
      kind: 'menu',
      node,
      order_index: node.order_index ?? 0,
    }));
    const assignmentEntries: Array<{ kind: 'assignment'; item: MenuAssignment; order_index: number }> = topAssignments.map(
      item => ({
        kind: 'assignment',
        item,
        order_index: item.order_index ?? 0,
      }),
    );
    return [...assignmentEntries, ...menuEntries]
      .sort((a, b) => {
        if (a.order_index === b.order_index) {
          if (a.kind === b.kind) {
            if (a.kind === 'menu' && b.kind === 'menu') return a.node.id - b.node.id;
            if (a.kind === 'assignment' && b.kind === 'assignment') return a.item.id - b.item.id;
          }
          return a.kind.localeCompare(b.kind);
        }
        return a.order_index - b.order_index;
      })
      .map(entry => (entry.kind === 'menu' ? { kind: 'menu', node: entry.node } : { kind: 'assignment', item: entry.item }));
  }, [tree, topAssignments]);
  const maxColumns = MAX_SUBMENU_COLUMNS;
  const columns = useMemo(() => {
    if (isNarrow) {
      const listNode = drillStack.length ? drillStack[drillStack.length - 1] : null;
      const entries = listNode ? buildEntries(listNode) : topLevelEntries;
      return [entries];
    }

    const next: MenuEntry[][] = [topLevelEntries];
    for (let depth = 0; depth < maxColumns; depth += 1) {
      const baseNode = activeNodes[depth];
      if (!baseNode) break;
      const listNode =
        depth === maxColumns - 1 && drillStack.length
          ? drillStack[drillStack.length - 1]
          : baseNode;
      const entries = buildEntries(listNode);
      if (!entries.length) break;
      next.push(entries);
    }
    return next;
  }, [isNarrow, topLevelEntries, activeNodes, drillStack, maxColumns]);

  const drillColumnIndex = isNarrow ? 0 : maxColumns;
  const activeNodeIds = useMemo(() => activeNodes.map(node => node?.id ?? null), [activeNodes]);

  function activateNodeAt(depth: number, node: MenuNode) {
    if (!hasSubmenu(node)) return;
    setActiveNodes(prev => {
      const next = [...prev];
      next[depth] = node;
      for (let i = depth + 1; i < next.length; i += 1) {
        next[i] = null;
      }
      return next;
    });
    setDrillStack([]);
  }

  function clearActiveFrom(depth: number) {
    setActiveNodes(prev => {
      const next = [...prev];
      for (let i = depth; i < next.length; i += 1) {
        next[i] = null;
      }
      return next;
    });
    setDrillStack([]);
  }

  function drillIntoNode(node: MenuNode) {
    if (!hasSubmenu(node)) return;
    setDrillStack(prev => [...prev, node]);
  }

  function handleNavigate() {
    setMenuOpen(false);
  }

  function handlePointerDown(event: ReactPointerEvent) {
    pointerTypeRef.current = event.pointerType || 'mouse';
  }

  function renderEntry(entry: MenuEntry, columnIndex: number) {
    const isTouchInput = pointerTypeRef.current !== 'mouse';
    const isDrillColumn = columnIndex === drillColumnIndex;
    const disableMenuNodeNavigation = isNarrow || columnIndex < MAX_SUBMENU_COLUMNS;

    if (entry.kind === 'menu') {
      const node = entry.node;
      const hasEntries = hasSubmenu(node);
      const isActive = activeNodeIds[columnIndex] === node.id;

      const handleMouseEnter = () => {
        if (isTouchInput || isDrillColumn) return;
        if (hasEntries) {
          activateNodeAt(columnIndex, node);
        } else {
          clearActiveFrom(columnIndex);
        }
      };

      const handleFocus = () => {
        if (hasEntries && !isDrillColumn) {
          activateNodeAt(columnIndex, node);
        }
      };

      const handleClick = (event: ReactMouseEvent) => {
        if (disableMenuNodeNavigation) {
          event.preventDefault();
          if (hasEntries) {
            if (isDrillColumn || isNarrow) {
              drillIntoNode(node);
            } else if (isTouchInput || !isActive) {
              activateNodeAt(columnIndex, node);
            }
          }
          return;
        }

        if (hasEntries) {
          if (isDrillColumn) {
            event.preventDefault();
            drillIntoNode(node);
            return;
          }
          if (isTouchInput && !isActive) {
            event.preventDefault();
            activateNodeAt(columnIndex, node);
            return;
          }
        }
        if (node.link_enabled === false) return;
        handleNavigate();
      };

      const linkClass = `nav-menu-link${node.link_enabled === false ? ' is-disabled' : ''}`;
      const renderAsLink = !disableMenuNodeNavigation && node.link_enabled !== false;

      return (
        <div key={`menu-entry-${node.id}`} className="nav-menu-item">
          <div className={`nav-menu-row${isActive ? ' is-active' : ''}`} onMouseEnter={handleMouseEnter}>
            {renderAsLink ? (
              <Link className={linkClass} href={`/s/${node.slug}`} onClick={handleClick} onFocus={handleFocus}>
                <span className="nav-menu-text">{node.name}</span>
              </Link>
            ) : (
              <button type="button" className={linkClass} onClick={handleClick} onFocus={handleFocus}>
                <span className="nav-menu-text">{node.name}</span>
              </button>
            )}
            <span className="nav-menu-type-icon nav-menu-type-icon--menu" />
          </div>
        </div>
      );
    }

    const item = entry.item;
    const { href, target, rel } = resolveAssignmentLink(item);
    const externalBehavior = item.open_behavior || 'current';
    const handleMouseEnter = () => {
      if (pointerTypeRef.current !== 'mouse' || columnIndex === drillColumnIndex) return;
      clearActiveFrom(columnIndex);
    };

    return (
      <div key={`menu-entry-${item.type}-${item.id}`} className="nav-menu-item">
        <div className="nav-menu-row" onMouseEnter={handleMouseEnter}>
          {item.type === 'external' && externalBehavior !== 'embed' ? (
            <PublicExternalLink
              className="nav-menu-link"
              href={item.url || '#'}
              openBehavior={externalBehavior}
              showPopupNote={item.show_popup_note}
              popupNoteHtml={item.popup_note_html}
              clipboardText={item.clipboard_text}
              linkName={item.title}
              onNavigate={handleNavigate}
            >
              <span className="nav-menu-text">{item.title}</span>
            </PublicExternalLink>
          ) : (
            <Link className="nav-menu-link" href={href} onClick={handleNavigate} target={target} rel={rel}>
              <span className="nav-menu-text">{item.title}</span>
            </Link>
          )}
          <span className={`nav-menu-type-icon nav-menu-type-icon--${item.type}`} />
        </div>
      </div>
    );
  }

  return (
    <div className="nav-search-bar">
      <div className="nav-search-inner">
        <div className="nav-menu" ref={menuRef}>
          <button
            className="nav-menu-button"
            type="button"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(prev => !prev)}
            onPointerDown={handlePointerDown}
          >
            <MenuButtonIcon className="nav-menu-button-icon" />
            <span>Menu</span>
            <CaretDownIcon className="nav-menu-button-caret" />
          </button>
          {menuOpen ? (
            <div className="nav-menu-dropdown" role="menu" onPointerDown={handlePointerDown}>
              {topLevelEntries.length ? (
                <div className="nav-menu-columns">
                  {columns.map((entries, columnIndex) => (
                    <div key={`menu-column-${columnIndex}`} className="nav-menu-column">
                      {columnIndex === drillColumnIndex && drillStack.length ? (
                        <button
                          className="nav-menu-back"
                          type="button"
                          onClick={() => setDrillStack(prev => prev.slice(0, -1))}
                        >
                          Back
                        </button>
                      ) : null}
                      {entries.map(entry => renderEntry(entry, columnIndex))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="nav-menu-empty">No menu items yet.</div>
              )}
            </div>
          ) : null}
        </div>
        <div className="nav-search" ref={searchRef}>
          <button
            className="nav-search-button"
            type="button"
            onClick={() => {
              setSearchOpen(true);
              searchInputRef.current?.focus();
            }}
            aria-label="Search"
          >
            <SearchIcon className="nav-search-button-icon" />
          </button>
          <input
            className="input nav-search-input"
            aria-label="Search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setSearchOpen(true)}
            ref={searchInputRef}
          />
          {showResults ? (
            <div className="nav-search-results">
              {loading ? <div className="nav-search-status">Searching...</div> : null}
              {error ? <div className="nav-search-status">{error}</div> : null}
              {!loading && !error ? (
                <>
                  {hasPageResults ? (
                    <div className="nav-search-section">
                      <div className="nav-search-label">Pages</div>
                      <div className="nav-search-items">
                        {results.pages.map(page => (
                          <Link
                            key={`search-page-${page.id}`}
                            className="nav-search-item"
                            href={`/p/${page.slug}`}
                            onClick={() => setSearchOpen(false)}
                          >
                            {page.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {hasFormResults ? (
                    <div className="nav-search-section">
                      <div className="nav-search-label">Forms</div>
                      <div className="nav-search-items">
                        {results.forms.map(form => (
                          <Link
                            key={`search-form-${form.id}`}
                            className="nav-search-item"
                            href={`/f/${form.slug}`}
                            onClick={() => setSearchOpen(false)}
                          >
                            {form.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {hasExternalLinkResults ? (
                    <div className="nav-search-section">
                      <div className="nav-search-label">External Links</div>
                      <div className="nav-search-items">
                        {results.externalLinks.map(link =>
                          link.open_behavior === 'embed' ? (
                            <Link
                              key={`search-external-${link.id}`}
                              className="nav-search-item"
                              href={`/x/${link.slug}`}
                              onClick={() => setSearchOpen(false)}
                            >
                              {link.name}
                            </Link>
                          ) : (
                            <PublicExternalLink
                              key={`search-external-${link.id}`}
                              className="nav-search-item"
                              href={link.url || '#'}
                              openBehavior={link.open_behavior}
                              showPopupNote={link.show_popup_note}
                              popupNoteHtml={link.popup_note_html}
                              clipboardText={link.clipboard_text}
                              linkName={link.name}
                              onNavigate={() => setSearchOpen(false)}
                            >
                              {link.name}
                            </PublicExternalLink>
                          ),
                        )}
                      </div>
                    </div>
                  ) : null}
                  {!hasAnySearchResults ? <div className="nav-search-empty">No matching results.</div> : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
